import { Editor } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ListIcon,
  ListOrderedIcon,
  CheckSquareIcon,
  LinkIcon,
  HighlighterIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  Heading1,
  Heading2,
  Heading3,
  CodeIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  editor: Editor;
  setLink: () => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}

const Toolbar = ({ editor, setLink }: ToolbarProps) => {
  const { t } = useTranslation();
  const ToolbarButton = ({ onClick, active, icon: Icon, title }: ToolbarButtonProps) => (
    <button
      className={`p-2 rounded-md transition-colors ${
        active ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
      title={title}
      type="button"
      onClick={onClick}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex flex-wrap gap-1 p-2">
        <div className="flex gap-1 items-center border-r border-gray-200 pr-2 mr-2">
          <ToolbarButton
            active={editor.isActive('heading', { level: 1 })}
            icon={Heading1}
            title={t('richTextEditor.heading1')}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          />
          <ToolbarButton
            active={editor.isActive('heading', { level: 2 })}
            icon={Heading2}
            title={t('richTextEditor.heading2')}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          />
          <ToolbarButton
            active={editor.isActive('heading', { level: 3 })}
            icon={Heading3}
            title={t('richTextEditor.heading3')}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          />
        </div>

        <div className="flex gap-1 items-center border-r border-gray-200 pr-2 mr-2">
          <ToolbarButton
            active={editor.isActive('bold')}
            icon={BoldIcon}
            title={t('richTextEditor.bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            active={editor.isActive('italic')}
            icon={ItalicIcon}
            title={t('richTextEditor.italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <ToolbarButton
            active={editor.isActive('underline')}
            icon={UnderlineIcon}
            title={t('richTextEditor.underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <ToolbarButton
            active={editor.isActive('strike')}
            icon={StrikethroughIcon}
            title={t('richTextEditor.strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />
        </div>

        <div className="flex gap-1 items-center border-r border-gray-200 pr-2 mr-2">
          <ToolbarButton
            active={editor.isActive('bulletList')}
            icon={ListIcon}
            title={t('richTextEditor.bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            active={editor.isActive('orderedList')}
            icon={ListOrderedIcon}
            title={t('richTextEditor.orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <ToolbarButton
            active={editor.isActive('taskList')}
            icon={CheckSquareIcon}
            title={t('richTextEditor.taskList')}
            onClick={() => editor.chain().focus().toggleTaskList().run()}
          />
        </div>

        <div className="flex gap-1 items-center border-r border-gray-200 pr-2 mr-2">
          <ToolbarButton
            active={editor.isActive('link')}
            icon={LinkIcon}
            title={t('richTextEditor.addLink')}
            onClick={setLink}
          />
          <ToolbarButton
            active={editor.isActive('highlight')}
            icon={HighlighterIcon}
            title={t('richTextEditor.highlight')}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
          />
          <ToolbarButton
            active={editor.isActive('codeBlock')}
            icon={CodeIcon}
            title={t('richTextEditor.codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          />
        </div>

        <div className="flex gap-1 items-center">
          <ToolbarButton
            active={editor.isActive({ textAlign: 'left' })}
            icon={AlignLeftIcon}
            title={t('richTextEditor.alignLeft')}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          />
          <ToolbarButton
            active={editor.isActive({ textAlign: 'center' })}
            icon={AlignCenterIcon}
            title={t('richTextEditor.alignCenter')}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          />
          <ToolbarButton
            active={editor.isActive({ textAlign: 'right' })}
            icon={AlignRightIcon}
            title={t('richTextEditor.alignRight')}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          />
        </div>
      </div>
    </div>
  );
};

export default Toolbar;
